import type { SpriteRenderer } from "./spriteRenderer";
import type { ParticleRenderer } from "./particles/particleRenderer";
import { getDevPanel } from "./devPanel";

export interface PerfRenderDeps {
	spriteRenderer: SpriteRenderer;
	particleRenderer: ParticleRenderer;
	boardThingCount: number;
}

export interface FrameTiming {
	renderMs: number;
	frameMs: number;
	darknessMs: number;
}

/** Per-session frame stats accumulator. DOM-free — pushes into the DevPanel singleton,
 *  so render() no-ops in prod (no panel). */
export class DevPerfOverlay {
	private frameCount: number = 0;
	private accumulatedMs: number = 0;
	private renderMsSum: number = 0;
	private frameMsSum: number = 0;
	private darknessMsSum: number = 0;
	private lastFps: number = 0;
	private lastRenderMs: number = 0;
	private lastTotalMs: number = 0;
	private lastDarknessMs: number = 0;
	private engineErrorCount: number = 0;

	noteEngineError(): void {
		this.engineErrorCount++;
	}

	getEngineErrorCount(): number {
		return this.engineErrorCount;
	}

	recordFrame(rawElapsed: number, timing: FrameTiming): void {
		this.frameCount++;
		this.accumulatedMs += rawElapsed;
		this.renderMsSum += timing.renderMs;
		this.frameMsSum += timing.frameMs;
		this.darknessMsSum += timing.darknessMs;
		if (this.accumulatedMs >= 1000) {
			this.lastFps = this.frameCount;
			this.lastRenderMs = this.renderMsSum / this.frameCount;
			this.lastTotalMs = this.frameMsSum / this.frameCount;
			this.lastDarknessMs = this.darknessMsSum / this.frameCount;
			this.frameCount = 0;
			this.accumulatedMs = 0;
			this.renderMsSum = 0;
			this.frameMsSum = 0;
			this.darknessMsSum = 0;
		}
	}

	render(deps: PerfRenderDeps): void {
		const panel = getDevPanel();
		if (!panel) return;

		panel.setEngineErrors(this.engineErrorCount);

		const { spriteRenderer, particleRenderer, boardThingCount } = deps;
		panel.updateStats([
			`FPS: ${this.lastFps}`,
			`Render: ${this.lastRenderMs.toFixed(1)}ms  Dark: ${this.lastDarknessMs.toFixed(1)}ms  Frame: ${this.lastTotalMs.toFixed(1)}ms`,
			`Floor draws: ${spriteRenderer.lastFloorDrawCalls}  Thing draws: ${spriteRenderer.lastThingDrawCalls}`,
			`Visible cells: ${spriteRenderer.lastVisibleCells}  Drawables: ${spriteRenderer.lastDrawableCount}`,
			`Board things: ${boardThingCount}  Particles: ${particleRenderer.particleCount}  Generators: ${particleRenderer.generatorCount}`,
		]);
	}

	dispose(): void {
		const panel = getDevPanel();
		if (!panel) return;
		panel.clearStats();
		panel.setEngineErrors(0);
	}
}
